import type { ColorGroup, Field } from "./types";

/**
 * German street set. Swap this file to play with a different set of names.
 * Rents are [base, 1 house, 2, 3, 4, hotel].
 */
type Rents = [number, number, number, number, number, number];
type StreetRow = [number, string, ColorGroup, number, Rents, number];

const street = ([index, name, group, price, rents, houseCost]: StreetRow): Field => ({
  kind: "STREET",
  index,
  name,
  group,
  price,
  rents,
  houseCost,
  mortgage: price / 2,
});

const railroad = (index: number, name: string): Field => ({
  kind: "RAILROAD",
  index,
  name,
  price: 200,
  rents: [25, 50, 100, 200],
  mortgage: 100,
});

const utility = (index: number, name: string): Field => ({
  kind: "UTILITY",
  index,
  name,
  price: 150,
  multipliers: [4, 10],
  mortgage: 75,
});

export const BOARD: readonly Field[] = [
  { kind: "GO", index: 0, name: "LOS" },
  street([1, "Badstraße", "purple", 60, [2, 10, 30, 90, 160, 250], 50]),
  { kind: "COMMUNITY", index: 2, name: "Gemeinschaftsfeld" },
  street([3, "Turmstraße", "purple", 60, [4, 20, 60, 180, 320, 450], 50]),
  { kind: "TAX", index: 4, name: "Einkommensteuer", amount: 200 },
  railroad(5, "Südbahnhof"),
  street([6, "Chausseestraße", "lightblue", 100, [6, 30, 90, 270, 400, 550], 50]),
  { kind: "CHANCE", index: 7, name: "Ereignisfeld" },
  street([8, "Elisenstraße", "lightblue", 100, [6, 30, 90, 270, 400, 550], 50]),
  street([9, "Poststraße", "lightblue", 120, [8, 40, 100, 300, 450, 600], 50]),
  { kind: "JAIL", index: 10, name: "Gefängnis / Nur zu Besuch" },
  street([11, "Seestraße", "pink", 140, [10, 50, 150, 450, 625, 750], 100]),
  utility(12, "Elektrizitätswerk"),
  street([13, "Hafenstraße", "pink", 140, [10, 50, 150, 450, 625, 750], 100]),
  street([14, "Neue Straße", "pink", 160, [12, 60, 180, 500, 700, 900], 100]),
  railroad(15, "Westbahnhof"),
  street([16, "Münchner Straße", "orange", 180, [14, 70, 200, 550, 750, 950], 100]),
  { kind: "COMMUNITY", index: 17, name: "Gemeinschaftsfeld" },
  street([18, "Wiener Straße", "orange", 180, [14, 70, 200, 550, 750, 950], 100]),
  street([19, "Berliner Straße", "orange", 200, [16, 80, 220, 600, 800, 1000], 100]),
  { kind: "FREE_PARKING", index: 20, name: "Frei Parken" },
  street([21, "Theaterstraße", "red", 220, [18, 90, 250, 700, 875, 1050], 150]),
  { kind: "CHANCE", index: 22, name: "Ereignisfeld" },
  street([23, "Museumstraße", "red", 220, [18, 90, 250, 700, 875, 1050], 150]),
  street([24, "Opernplatz", "red", 240, [20, 100, 300, 750, 925, 1100], 150]),
  railroad(25, "Nordbahnhof"),
  street([26, "Lessingstraße", "yellow", 260, [22, 110, 330, 800, 975, 1150], 150]),
  street([27, "Schillerstraße", "yellow", 260, [22, 110, 330, 800, 975, 1150], 150]),
  utility(28, "Wasserwerk"),
  street([29, "Goethestraße", "yellow", 280, [24, 120, 360, 850, 1025, 1200], 150]),
  { kind: "GO_TO_JAIL", index: 30, name: "Gehen Sie in das Gefängnis" },
  street([31, "Rathausplatz", "green", 300, [26, 130, 390, 900, 1100, 1275], 200]),
  street([32, "Hauptstraße", "green", 300, [26, 130, 390, 900, 1100, 1275], 200]),
  { kind: "COMMUNITY", index: 33, name: "Gemeinschaftsfeld" },
  street([34, "Bahnhofstraße", "green", 320, [28, 150, 450, 1000, 1200, 1400], 200]),
  railroad(35, "Hauptbahnhof"),
  { kind: "CHANCE", index: 36, name: "Ereignisfeld" },
  street([37, "Parkstraße", "darkblue", 350, [35, 175, 500, 1100, 1300, 1500], 200]),
  { kind: "TAX", index: 38, name: "Zusatzsteuer", amount: 100 },
  street([39, "Schlossallee", "darkblue", 400, [50, 200, 600, 1400, 1700, 2000], 200]),
];

export const COLOR_GROUPS: readonly ColorGroup[] = [
  "purple",
  "lightblue",
  "pink",
  "orange",
  "red",
  "yellow",
  "green",
  "darkblue",
];

export interface GroupInfo {
  name: string;
  color: string;
  fields: number[];
  houseCost: number;
}

export const GROUP_INFO: Record<ColorGroup, GroupInfo> = (() => {
  const meta: Record<ColorGroup, { name: string; color: string }> = {
    purple: { name: "Lila", color: "#8a5aa8" },
    lightblue: { name: "Hellblau", color: "#6cc4ea" },
    pink: { name: "Pink", color: "#e0559a" },
    orange: { name: "Orange", color: "#f08c2e" },
    red: { name: "Rot", color: "#d63f3f" },
    yellow: { name: "Gelb", color: "#f0cb2f" },
    green: { name: "Grün", color: "#2f9a57" },
    darkblue: { name: "Dunkelblau", color: "#2a49a3" },
  };
  const out = {} as Record<ColorGroup, GroupInfo>;
  for (const g of COLOR_GROUPS) out[g] = { ...meta[g], fields: [], houseCost: 0 };
  for (const f of BOARD) {
    if (f.kind === "STREET") {
      out[f.group].fields.push(f.index);
      out[f.group].houseCost = f.houseCost;
    }
  }
  return out;
})();

export const OWNABLE_FIELDS: readonly number[] = BOARD.filter(
  (f) => f.kind === "STREET" || f.kind === "RAILROAD" || f.kind === "UTILITY",
).map((f) => f.index);

export const STREET_FIELDS: readonly number[] = BOARD.filter((f) => f.kind === "STREET").map((f) => f.index);
export const RAILROAD_FIELDS: readonly number[] = BOARD.filter((f) => f.kind === "RAILROAD").map((f) => f.index);
export const UTILITY_FIELDS: readonly number[] = BOARD.filter((f) => f.kind === "UTILITY").map((f) => f.index);
